import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Gift, Power, Save } from 'lucide-react';
import {
  Alert,
  Button,
  Badge,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  MetricCard,
  Spinner,
} from '@pnl/ui';
import { PnlApiError } from '@pnl/sdk';
import { api } from '../lib/api';

function fmtBrl(cents: number): string {
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/** Converte input decimal (reais/dólares) → cents; null/inválido → 0. */
function toCents(v: string): number {
  const n = Number(v.trim().replace(',', '.'));
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : 0;
}

/** Valida % inteiro 0-100 digitado; null = inválido (botão salvar desabilita). */
function parsePercent(v: string): number | null {
  if (v.trim() === '') return null;
  const n = Number(v.trim());
  return Number.isInteger(n) && n >= 0 && n <= 100 ? n : null;
}

export function ReferralsPage() {
  const qc = useQueryClient();
  const stats = useQuery({
    queryKey: ['admin', 'referrals'],
    queryFn: () => api.getReferralStats(),
  });
  const settings = useQuery({
    queryKey: ['admin', 'settings', 'referral'],
    queryFn: () => api.getReferralSettings(),
  });

  const [enabled, setEnabled] = useState(false);
  const [b3, setB3] = useState('');
  const [forex, setForex] = useState('');
  const [discount, setDiscount] = useState('');
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (settings.data && !initialized) {
      setEnabled(settings.data.enabled);
      setB3((settings.data.creditB3Cents / 100).toFixed(2));
      setForex((settings.data.creditForexUsdCents / 100).toFixed(2));
      setDiscount(String(settings.data.discountPercent));
      setInitialized(true);
    }
  }, [settings.data, initialized]);

  const discountParsed = parsePercent(discount);

  const save = useMutation({
    mutationFn: () =>
      api.setReferralSettings({
        enabled,
        creditB3Cents: toCents(b3),
        creditForexUsdCents: toCents(forex),
        discountPercent: discountParsed ?? 0,
      }),
    onSuccess: (data) => {
      qc.setQueryData(['admin', 'settings', 'referral'], data);
      void qc.invalidateQueries({ queryKey: ['admin', 'referrals'] });
    },
  });

  const s = stats.data;

  return (
    <div className="space-y-6">
      <div>
        <p className="text-2xs text-brand-500 font-semibold uppercase tracking-widest">Catálogo</p>
        <h1 className="font-display text-ink-900 mt-1 text-3xl font-semibold tracking-tight">
          Programa de Indicação
        </h1>
        <p className="text-ink-500 mt-1 text-sm">
          Cada trader tem um cupom pessoal; a cada plano vendido com o cupom, o indicador ganha
          crédito (usável como desconto, não sacável).
        </p>
      </div>

      {/* Config */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Gift size={16} className="text-brand-500" />
            <CardTitle>Configuração</CardTitle>
          </div>
        </CardHeader>
        <CardBody>
          {settings.isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-ink-900 text-sm font-semibold">Programa ativo</p>
                    <Badge tone={enabled ? 'success' : 'neutral'}>
                      {enabled ? 'Ligado' : 'Desligado'}
                    </Badge>
                  </div>
                  <p className="text-ink-500 mt-1 text-xs">
                    Desligado, os cupons de indicação param de gerar crédito (o saldo já acumulado
                    continua válido).
                  </p>
                </div>
                <Button
                  size="sm"
                  variant={enabled ? 'secondary' : 'primary'}
                  leadingIcon={enabled ? <Power size={13} /> : <Check size={13} />}
                  onClick={() => setEnabled((v) => !v)}
                >
                  {enabled ? 'Desligar' : 'Ligar'}
                </Button>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <label className="text-2xs text-ink-500 font-semibold uppercase tracking-wide">
                    Crédito por indicação B3 (R$)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={b3}
                    onChange={(e) => setB3(e.target.value)}
                    className="border-ink-200 text-ink-900 focus:border-brand-500 mt-1 w-full rounded-md border bg-white px-3 py-2 text-sm focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-2xs text-ink-500 font-semibold uppercase tracking-wide">
                    Crédito por indicação Forex (US$)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={forex}
                    onChange={(e) => setForex(e.target.value)}
                    className="border-ink-200 text-ink-900 focus:border-brand-500 mt-1 w-full rounded-md border bg-white px-3 py-2 text-sm focus:outline-none"
                  />
                  <p className="text-ink-400 text-2xs mt-1">
                    Convertido pra BRL na cotação da compra ao creditar.
                  </p>
                </div>
                <div>
                  <label className="text-2xs text-ink-500 font-semibold uppercase tracking-wide">
                    Desconto padrão do cupom (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    value={discount}
                    onChange={(e) => setDiscount(e.target.value)}
                    className="border-ink-200 text-ink-900 focus:border-brand-500 mt-1 w-full rounded-md border bg-white px-3 py-2 text-sm focus:outline-none"
                  />
                  <p className="text-ink-400 text-2xs mt-1">
                    Aplicado na criação de novos cupons de indicação (0-100).
                  </p>
                </div>
              </div>
              {save.error && (
                <Alert tone="danger" title="Falhou">
                  {save.error instanceof PnlApiError ? save.error.api.message : 'Erro ao salvar'}
                </Alert>
              )}
              <div className="flex justify-end">
                <Button
                  leadingIcon={<Save size={14} />}
                  loading={save.isPending}
                  disabled={discountParsed === null}
                  onClick={() => save.mutate()}
                >
                  Salvar
                </Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Estatísticas */}
      <section className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <MetricCard
          label="Crédito distribuído"
          value={stats.isLoading ? '…' : fmtBrl(s?.totalCreditedCents ?? 0)}
          hint="total ganho por indicações"
        />
        <MetricCard
          label="Crédito usado"
          value={stats.isLoading ? '…' : fmtBrl(s?.totalSpentCents ?? 0)}
          hint="abatido em compras"
        />
        <MetricCard
          label="Vendas por indicação"
          value={stats.isLoading ? '…' : String(s?.totalReferralSales ?? 0)}
          hint="planos vendidos via cupom"
        />
      </section>

      {/* Ranking */}
      <Card>
        <CardHeader>
          <CardTitle>Ranking de indicadores</CardTitle>
        </CardHeader>
        <CardBody>
          {stats.isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : !s || s.ranking.length === 0 ? (
            <p className="text-ink-500 py-6 text-center text-sm">
              Nenhuma indicação registrada ainda.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-ink-500 text-2xs uppercase tracking-wide">
                  <th className="pb-2 text-left font-semibold">#</th>
                  <th className="pb-2 text-left font-semibold">Trader</th>
                  <th className="pb-2 text-right font-semibold">Indicações</th>
                  <th className="pb-2 text-right font-semibold">Crédito ganho</th>
                </tr>
              </thead>
              <tbody className="divide-ink-100 divide-y">
                {s.ranking.map((r, i) => (
                  <tr key={r.traderId}>
                    <td className="text-ink-400 py-2.5">{i + 1}</td>
                    <td className="py-2.5">
                      <p className="text-ink-900 font-medium">{r.name}</p>
                      <p className="text-ink-400 text-xs">{r.email}</p>
                    </td>
                    <td className="text-ink-900 py-2.5 text-right tabular-nums">{r.referrals}</td>
                    <td className="text-ink-900 py-2.5 text-right font-semibold tabular-nums">
                      {fmtBrl(r.earnedCents)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      {/* Cupons de indicação (edição individual do %) */}
      <ReferralCouponsCard />
    </div>
  );
}

/** Tabela dos cupons de indicação com o % de desconto editável por linha. */
function ReferralCouponsCard() {
  const qc = useQueryClient();
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const coupons = useQuery({
    queryKey: ['admin', 'referrals', 'coupons'],
    queryFn: () => api.listReferralCoupons(),
  });

  const update = useMutation({
    mutationFn: ({ id, discountValue }: { id: string; discountValue: number }) =>
      api.updateReferralCoupon(id, { discountValue }),
    onSuccess: (_data, vars) => {
      setDrafts((d) => {
        const next = { ...d };
        delete next[vars.id];
        return next;
      });
      void qc.invalidateQueries({ queryKey: ['admin', 'referrals', 'coupons'] });
    },
    onError: (e) =>
      setError(e instanceof PnlApiError ? e.api.message : 'Erro ao salvar o desconto'),
  });

  const items = coupons.data?.items ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Cupons de indicação</CardTitle>
      </CardHeader>
      <CardBody>
        {error && (
          <div className="mb-4">
            <Alert tone="danger">{error}</Alert>
          </div>
        )}
        {coupons.isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : coupons.isError ? (
          <Alert tone="danger">
            Não foi possível carregar os cupons de indicação — recarregue a página.
          </Alert>
        ) : items.length === 0 ? (
          <p className="text-ink-500 py-6 text-center text-sm">
            Nenhum cupom de indicação criado ainda (cada trader ganha o seu ao acessar a
            plataforma).
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-ink-500 text-2xs uppercase tracking-wide">
                <th className="pb-2 text-left font-semibold">Cupom</th>
                <th className="pb-2 text-left font-semibold">Trader</th>
                <th className="pb-2 text-right font-semibold">Usos</th>
                <th className="pb-2 text-left font-semibold">Status</th>
                <th className="pb-2 text-right font-semibold">Desconto (%)</th>
              </tr>
            </thead>
            <tbody className="divide-ink-100 divide-y">
              {items.map((c) => {
                const draft = drafts[c.id];
                const parsed = draft === undefined ? c.discountValue : parsePercent(draft);
                const changed =
                  draft !== undefined && parsed !== null && parsed !== c.discountValue;
                const saving = update.isPending && update.variables?.id === c.id;
                return (
                  <tr key={c.id}>
                    <td className="py-2.5">
                      <span className="text-brand-700 font-mono font-semibold">{c.code}</span>
                    </td>
                    <td className="py-2.5">
                      <p className="text-ink-900 font-medium">{c.traderName ?? '—'}</p>
                      <p className="text-ink-400 text-xs">{c.traderEmail}</p>
                    </td>
                    <td className="text-ink-900 py-2.5 text-right tabular-nums">{c.usedCount}</td>
                    <td className="py-2.5">
                      <Badge tone={c.active ? 'success' : 'neutral'}>
                        {c.active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </td>
                    <td className="py-2.5">
                      <div className="flex items-center justify-end gap-2">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          value={draft ?? String(c.discountValue)}
                          onChange={(e) => setDrafts((d) => ({ ...d, [c.id]: e.target.value }))}
                          aria-label={`Desconto do cupom ${c.code} (%)`}
                          className="border-ink-200 text-ink-900 focus:border-brand-500 w-20 rounded-md border bg-white px-2 py-1.5 text-right text-sm tabular-nums focus:outline-none"
                        />
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={!changed}
                          loading={saving}
                          onClick={() => {
                            setError(null);
                            if (parsed !== null) update.mutate({ id: c.id, discountValue: parsed });
                          }}
                        >
                          Salvar
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </CardBody>
    </Card>
  );
}
